import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { SectionHeading, Reveal, EASE } from "@/components/Reveal";

const PROVINCES = [
  { code: "GP", name: "Gauteng", hub: "Johannesburg / Pretoria Hubs", focus: "High-density retail distribution & e-commerce same-day subcontracting" },
  { code: "WC", name: "Western Cape", hub: "Cape Town & Winelands Corridor", focus: "Port logistics transfer, regional linehaul & store replenishment" },
  { code: "KZN", name: "KwaZulu-Natal", hub: "Durban / Pinetown Corridor", focus: "Harbour-to-distribution centres & N3 inter-provincial routes" },
  { code: "EC", name: "Eastern Cape", hub: "Gqeberha / East London", focus: "Automotive feeder routes & coastal regional deliveries" },
  { code: "FS", name: "Free State", hub: "Bloemfontein Central Depot", focus: "Cross-border transit hub & central depot sorting runs" },
  { code: "MP", name: "Mpumalanga", hub: "Nelspruit / Witbank", focus: "Industrial supply & regional retail delivery" },
  { code: "LP", name: "Limpopo", hub: "Polokwane Hub", focus: "Cross-border dispatch & Northern corridor scheduled drops" },
  { code: "NW", name: "North West", hub: "Rustenburg / Potchefstroom", focus: "Mining supply runs & secondary city routes" },
  { code: "NC", name: "Northern Cape", hub: "Kimberley / Upington", focus: "Long-distance regional transit & scheduled runs" },
];

export const Coverage = () => {
  const [active, setActive] = useState(0);
  const province = PROVINCES[active];

  return (
    <section id="coverage" className="relative scroll-mt-24 overflow-hidden py-20 md:py-28" data-testid="coverage-section">
      <img
        src={IMAGES.highway}
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-[#080C14]/[0.93]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Nationwide Coverage"
          title="Nine provinces. One contract."
          intro="From metros to secondary cities and long-distance regional corridors — select a province to see how NGCONDE runs in your area."
        />

        <Reveal delay={0.1}>
          <div className="mt-12 grid grid-cols-3 gap-2 md:grid-cols-5 lg:grid-cols-9">
            {PROVINCES.map((p, i) => (
              <button
                key={p.code}
                onClick={() => setActive(i)}
                data-testid="province-item"
                aria-pressed={active === i}
                className={`rounded-xl border px-2 py-4 text-center transition-all duration-300 active:scale-95 ${
                  active === i
                    ? "border-[#FF6B00] bg-[#FF6B00] shadow-[0_0_24px_rgba(255,107,0,0.35)]"
                    : "border-white/10 bg-[#0D1424]/80 hover:border-[#FF6B00]/50"
                }`}
              >
                <span
                  className={`block font-display text-lg font-black tracking-tight md:text-xl ${
                    active === i ? "text-[#080C14]" : "text-white"
                  }`}
                >
                  {p.code}
                </span>
                <span
                  className={`mt-1 block font-mono text-[9px] uppercase tracking-[0.14em] ${
                    active === i ? "text-[#080C14]/80" : "text-slate-400"
                  }`}
                >
                  {p.name}
                </span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 min-h-[150px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={province.code}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: EASE }}
              data-testid="province-detail-panel"
              className="rounded-2xl border border-white/10 bg-[#0D1424]/90 p-6 backdrop-blur md:p-8"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-2 rounded-full bg-[#FF6B00]/10 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF6B00]">
                  <MapPin className="h-3.5 w-3.5" />
                  {province.code} — {province.name}
                </span>
              </div>
              <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
                Operating hub
              </p>
              <h3 className="mt-1 font-display text-xl font-extrabold uppercase tracking-tight text-white md:text-2xl">
                {province.hub}
              </h3>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 md:text-base">
                {province.focus}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
